import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { testimonials } from '../../../mocks/testimonials';

export default function TestimonialsPreview() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const featured = testimonials.slice(0, 3);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-amber-500 font-semibold text-sm uppercase tracking-widest mb-3">{t('testimonials.badge')}</p>
          <h2 className="text-4xl font-bold text-gray-900 font-playfair mb-4">{t('testimonials.title')}</h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto">
            {t('testimonials.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
          {featured.map((review, idx) => (
            <div
              key={review.id}
              className={`bg-white rounded-xl p-6 border border-gray-100 transition-all duration-500 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${idx * 150}ms` }}
            >
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="w-4 h-4 flex items-center justify-center">
                    <i className={`ri-star-${i < review.rating ? 'fill' : 'line'} text-amber-400 text-sm`}></i>
                  </div>
                ))}
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-5 line-clamp-4">
                &ldquo;{review.comment}&rdquo;
              </p>
              <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                <div className="w-10 h-10 flex items-center justify-center rounded-full overflow-hidden flex-shrink-0">
                  <img src={review.avatar} alt={review.name} className="w-full h-full object-cover object-top" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{review.name}</p>
                  <p className="text-gray-400 text-xs">{review.location} &middot; {review.date}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className={`text-center mt-10 transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <Link
            to="/testimonials"
            className="inline-flex items-center gap-2 border-2 border-emerald-700 text-emerald-700 hover:bg-emerald-700 hover:text-white font-semibold px-7 py-3 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            {t('testimonials.readAll')}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
